import React from "react";
import {
  Modal,
  Button,
  Badge,
  Row,
  Col,
  Image,
  Tab,
  Nav,
  Accordion,
  Card,
  ListGroup,
} from "react-bootstrap";
import {
  IoClose,
  IoCalendar,
  IoEye,
  IoHeart,
  IoChatbubble,
  IoPerson,
  IoDocumentText,
  IoStar,
  IoShareSocial,
  IoPrint,
  IoDownload,
  IoBusiness,
  IoLocation,
  IoCash,
  IoBag,
  IoCheckmarkCircle,
  IoAlertCircle,
  IoCloudUpload,
  IoTrash,
} from "react-icons/io5";
import {
  FaBook,
  FaTag,
  FaClock,
  FaUserMd,
  FaBriefcase,
  FaGraduationCap,
} from "react-icons/fa";
import { formatDistanceToNow, format } from "date-fns";
import { id } from "date-fns/locale";

const CareerViewModal = ({ show, onClose, career }) => {
  if (!career) return null;

  // Format tanggal
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return format(date, "dd MMMM yyyy, HH:mm", { locale: id });
    } catch {
      return dateString;
    }
  };

  // Format tanggal tanpa waktu (untuk deadline)
  const formatSimpleDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return format(date, "dd MMMM yyyy", { locale: id });
    } catch {
      return dateString;
    }
  };

  // Time ago
  const getTimeAgo = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, {
        addSuffix: true,
        locale: id,
      });
    } catch {
      return "";
    }
  };

  // Hitung hari tersisa
  const calculateDaysRemaining = (deadline) => {
    if (!deadline) return null;
    const deadlineDate = new Date(deadline);
    const today = new Date();
    const timeDiff = deadlineDate.getTime() - today.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  };

  const daysRemaining = calculateDaysRemaining(career.application_deadline);

  // Format gaji
  const formatSalary = (amount) => {
    if (!amount) return "-";
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Status badge config
  const getStatusConfig = (isActive) => {
    return isActive
      ? { variant: "success", label: "Active", icon: IoCheckmarkCircle }
      : { variant: "secondary", label: "Inactive", icon: IoAlertCircle };
  };

  // Employment type config
  const getEmploymentTypeConfig = (type) => {
    const configs = {
      "Full Time": { variant: "primary", label: "Full Time" },
      "Part Time": { variant: "info", label: "Part Time" },
      Contract: { variant: "warning", label: "Contract" },
      Internship: { variant: "success", label: "Internship" },
      Freelance: { variant: "secondary", label: "Freelance" },
    };
    return configs[type] || { variant: "light", label: type };
  };

  // Position level config
  const getPositionLevelConfig = (level) => {
    const configs = {
      Intern: { variant: "secondary", label: "Intern" },
      Junior: { variant: "info", label: "Junior" },
      Associate: { variant: "info", label: "Associate" },
      "Mid Level": { variant: "warning", label: "Mid Level" },
      Senior: { variant: "primary", label: "Senior" },
      Lead: { variant: "danger", label: "Lead" },
      Manager: { variant: "danger", label: "Manager" },
      Director: { variant: "dark", label: "Director" },
      "Senior Specialist": { variant: "primary", label: "Senior Specialist" },
    };
    return configs[level] || { variant: "light", label: level };
  };

  const statusConfig = getStatusConfig(career.is_active);
  const employmentTypeConfig = getEmploymentTypeConfig(career.employment_type);
  const positionLevelConfig = getPositionLevelConfig(career.position_level);

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      centered
      backdrop="static"
      className="career-view-modal"
    >
      <Modal.Header className="border-bottom-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <IoBag size={24} className="text-primary" />
          <span>Detail Lowongan Kerja</span>
        </Modal.Title>
        <Button variant="link" onClick={onClose} className="p-0">
          <IoClose size={28} />
        </Button>
      </Modal.Header>

      <Modal.Body className="pt-0">
        {/* Header dengan judul dan badges */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="flex-grow-1">
              <h2 className="mb-2">{career.title}</h2>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Badge
                  bg="light"
                  text="dark"
                  className="d-flex align-items-center gap-1"
                >
                  <IoBusiness size={14} /> {career.specialization}
                </Badge>
                <Badge
                  bg="light"
                  text="dark"
                  className="d-flex align-items-center gap-1"
                >
                  <IoLocation size={14} /> {career.location}
                </Badge>
              </div>
            </div>
            <div className="d-flex flex-column gap-2 align-items-end">
              <Badge
                bg={statusConfig.variant}
                className="px-3 py-2 d-flex align-items-center gap-1"
              >
                <statusConfig.icon size={14} /> {statusConfig.label}
              </Badge>
              {career.is_urgent && (
                <Badge bg="danger" className="px-3 py-2">
                  URGENT
                </Badge>
              )}
            </div>
          </div>

          <div className="d-flex flex-wrap gap-3 text-muted small mb-4">
            <span className="d-flex align-items-center gap-1">
              <IoCalendar /> Created: {formatDate(career.created_at)}
            </span>
            {career.updated_at && (
              <span className="d-flex align-items-center gap-1">
                <FaClock /> Updated: {getTimeAgo(career.updated_at)}
              </span>
            )}
            {career.application_deadline && (
              <span
                className={`d-flex align-items-center gap-1 ${daysRemaining <= 7 ? "text-warning" : "text-success"}`}
              >
                <IoCalendar /> Deadline:{" "}
                {formatSimpleDate(career.application_deadline)}
                {daysRemaining !== null && (
                  <span className="ms-1">
                    (
                    {daysRemaining > 0
                      ? `${daysRemaining} hari tersisa`
                      : "Sudah lewat"}
                    )
                  </span>
                )}
              </span>
            )}
          </div>
        </div>

        {/* Info Boxes */}
        <Row className="mb-4 g-3">
          <Col md={3} sm={6}>
            <Card className="border-0 bg-light h-100">
              <Card.Body className="d-flex align-items-center gap-3">
                <div className="bg-primary bg-opacity-10 p-2 rounded">
                  <IoCash size={24} className="text-primary" />
                </div>
                <div>
                  <small className="text-muted d-block">Gaji</small>
                  <div className="fw-bold">
                    {formatSalary(career.salary_min)}
                    {career.salary_max && (
                      <> - {formatSalary(career.salary_max)}</>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} sm={6}>
            <Card className="border-0 bg-light h-100">
              <Card.Body className="d-flex align-items-center gap-3">
                <div className="bg-info bg-opacity-10 p-2 rounded">
                  <FaBriefcase size={20} className="text-info" />
                </div>
                <div>
                  <small className="text-muted d-block">Jenis Pekerjaan</small>
                  <Badge bg={employmentTypeConfig.variant}>
                    {employmentTypeConfig.label}
                  </Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} sm={6}>
            <Card className="border-0 bg-light h-100">
              <Card.Body className="d-flex align-items-center gap-3">
                <div className="bg-warning bg-opacity-10 p-2 rounded">
                  <FaGraduationCap size={20} className="text-warning" />
                </div>
                <div>
                  <small className="text-muted d-block">Level Posisi</small>
                  <Badge bg={positionLevelConfig.variant}>
                    {positionLevelConfig.label}
                  </Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} sm={6}>
            <Card className="border-0 bg-light h-100">
              <Card.Body className="d-flex align-items-center gap-3">
                <div className="bg-success bg-opacity-10 p-2 rounded">
                  <IoPerson size={24} className="text-success" />
                </div>
                <div>
                  <small className="text-muted d-block">Posisi Tersedia</small>
                  <div className="fw-bold fs-5">
                    {career.available_positions} posisi
                    {career.current_applicants > 0 && (
                      <small className="text-muted d-block">
                        {career.current_applicants} pelamar
                      </small>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabs untuk detail */}
        <Tab.Container defaultActiveKey="description">
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey="description">
                <IoDocumentText /> Deskripsi
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="requirements">
                <IoCheckmarkCircle /> Persyaratan
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="skills">
                <FaUserMd /> Keahlian
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="details">
                <FaBook /> Detail
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* Tab Deskripsi */}
            <Tab.Pane eventKey="description">
              <Card className="border-0">
                <Card.Body>
                  <h5 className="mb-3">Deskripsi Pekerjaan</h5>
                  <div className="p-3 bg-light rounded mb-4">
                    {career.description ? (
                      <div
                        className="quill-content"
                        style={{
                          lineHeight: 1.8,
                          fontSize: "1.05rem",
                          fontFamily:
                            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: career.description,
                        }}
                      />
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <IoDocumentText size={48} className="mb-3 opacity-50" />
                        <p>Tidak ada deskripsi tersedia</p>
                      </div>
                    )}
                  </div>

                  {career.benefits && (
                    <>
                      <h5 className="mb-3">Benefit & Fasilitas</h5>
                      <div className="p-3 bg-light rounded">
                        <div
                          className="quill-content"
                          style={{ lineHeight: 1.8 }}
                          dangerouslySetInnerHTML={{
                            __html: career.benefits,
                          }}
                        />
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Tab.Pane>

            {/* Tab Persyaratan */}
            <Tab.Pane eventKey="requirements">
              <Card className="border-0">
                <Card.Body>
                  <h5 className="mb-3">Persyaratan & Kualifikasi</h5>
                  <div className="p-3 bg-light rounded">
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        lineHeight: "1.8",
                        fontSize: "1.05rem",
                      }}
                    >
                      {career.requirements}
                    </div>
                  </div>

                  <Row className="mt-4 g-3">
                    <Col md={6}>
                      <div className="p-3 border rounded">
                        <h6 className="d-flex align-items-center gap-2 mb-3">
                          <FaClock /> Pengalaman
                        </h6>
                        <div className="fw-bold fs-4">
                          {career.experience_required || 0} tahun
                        </div>
                        <small className="text-muted">
                          Pengalaman minimum yang dibutuhkan
                        </small>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="p-3 border rounded">
                        <h6 className="d-flex align-items-center gap-2 mb-3">
                          <IoBusiness /> Lingkungan Kerja
                        </h6>
                        <div className="fw-bold">{career.work_environment}</div>
                        <small className="text-muted">
                          {career.remote_allowed && "Remote work diperbolehkan"}
                          {!career.remote_allowed && "On-site work required"}
                        </small>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab.Pane>

            {/* Tab Keahlian */}
            <Tab.Pane eventKey="skills">
              <Card className="border-0">
                <Card.Body>
                  <h5 className="mb-4">Keahlian yang Dibutuhkan</h5>

                  {career.required_skills &&
                  career.required_skills.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2 mb-4">
                      {career.required_skills.map((skill, index) => (
                        <Badge
                          key={index}
                          bg="primary"
                          className="px-3 py-2 fs-6"
                          style={{ fontSize: "0.9rem" }}
                        >
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted">
                      Tidak ada keahlian khusus yang ditentukan
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Tab.Pane>

            {/* Tab Detail */}
            <Tab.Pane eventKey="details">
              <Row className="g-3">
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Body>
                      <Card.Title className="d-flex align-items-center gap-2 mb-3">
                        <IoPerson /> Informasi Lowongan
                      </Card.Title>
                      <ListGroup variant="flush">
                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Spesialisasi:</span>
                          <span className="fw-semibold">
                            {career.specialization}
                          </span>
                        </ListGroup.Item>
                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Lokasi:</span>
                          <span className="fw-semibold">{career.location}</span>
                        </ListGroup.Item>
                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Jumlah Posisi:</span>
                          <span className="fw-semibold">
                            {career.available_positions}
                          </span>
                        </ListGroup.Item>
                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Pelamar Saat Ini:</span>
                          <span className="fw-semibold">
                            {career.current_applicants || 0}
                          </span>
                        </ListGroup.Item>
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="h-100">
                    <Card.Body>
                      <Card.Title className="d-flex align-items-center gap-2 mb-3">
                        <FaClock /> Informasi Tambahan
                      </Card.Title>
                      <ListGroup variant="flush">
                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Diupdate Oleh:</span>
                          <span className="fw-semibold">
                            {career.updated_by || "Belum diupdate"}
                          </span>
                        </ListGroup.Item>
                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Lingkungan Kerja:</span>
                          <span className="fw-semibold">
                            {career.work_environment}
                          </span>
                        </ListGroup.Item>
                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Remote Work:</span>
                          <Badge
                            bg={career.remote_allowed ? "success" : "secondary"}
                          >
                            {career.remote_allowed
                              ? "Diperbolehkan"
                              : "Tidak Diperbolehkan"}
                          </Badge>
                        </ListGroup.Item>
                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Status:</span>
                          <Badge bg={statusConfig.variant}>
                            {statusConfig.label}
                          </Badge>
                        </ListGroup.Item>
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Timeline Section */}
              <Card className="mt-3">
                <Card.Body>
                  <Card.Title className="d-flex align-items-center gap-2 mb-3">
                    <IoCalendar /> Timeline
                  </Card.Title>
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <div>
                        <span className="text-muted">Created:</span>
                        <div className="small">
                          {formatDate(career.created_at)}
                        </div>
                      </div>
                      <div className="text-end">
                        <small className="text-muted">
                          {getTimeAgo(career.created_at)}
                        </small>
                      </div>
                    </div>

                    {career.updated_at && (
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                        <div>
                          <span className="text-muted">Last Updated:</span>
                          <div className="small">
                            {formatDate(career.updated_at)}
                          </div>
                        </div>
                        <div className="text-end">
                          <small className="text-muted">
                            {getTimeAgo(career.updated_at)}
                          </small>
                        </div>
                      </div>
                    )}

                    {career.application_deadline && (
                      <div className="d-flex justify-content-between align-items-center py-2">
                        <div>
                          <span className="text-muted">
                            Application Deadline:
                          </span>
                          <div className="small">
                            {formatSimpleDate(career.application_deadline)}
                          </div>
                        </div>
                        <div className="text-end">
                          <small
                            className={
                              daysRemaining <= 7
                                ? "text-warning"
                                : "text-success"
                            }
                          >
                            {daysRemaining > 0
                              ? `${daysRemaining} hari tersisa`
                              : "Sudah lewat"}
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>

      <Modal.Footer className="border-top-0">
        <Button variant="secondary" onClick={onClose}>
          <IoClose /> Tutup
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CareerViewModal;
